import React, { useMemo, useState, useRef } from 'react';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { MapWithMarkerProps } from '../../../../types/questionForm';
import { ESGScoreDisplay } from '../../Badge';
import { createPortal } from 'react-dom';

// Custom Map Component with ESG Score Marker(s)
export const MapWithMarker: React.FC<MapWithMarkerProps> = ({
  question,
  value,
  onChange,
  latitude: propLatitude,
  longitude: propLongitude,
  hotel,
  markers,
  defaultZoom = 15,
  markerComponent,
  onMarkerClick,
  viewType,
  inProgress: globalInProgress = false,
}) => {
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY || '';
  const [hoveredMarkerIndex, setHoveredMarkerIndex] = useState<number | null>(
    null
  );
  const [tooltipPosition, setTooltipPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const markerRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Prepare marker data - either from markers array or single marker props
  const markerData = useMemo(() => {
    if (markers && markers.length > 0) {
      return markers;
    }

    // Fallback to single marker from props
    const latitude = propLatitude ?? question?.config?.latitude ?? 0;
    const longitude = propLongitude ?? question?.config?.longitude ?? 0;

    return [
      {
        latitude,
        longitude,
        hotel,
        inProgress: globalInProgress,
      },
    ];
  }, [
    markers,
    propLatitude,
    propLongitude,
    question?.config?.latitude,
    question?.config?.longitude,
    hotel,
    globalInProgress,
  ]);

  // Calculate center point for map (average of all markers)
  const center = useMemo(() => {
    if (markerData.length === 0) {
      return { lat: 0, lng: 0 };
    }

    // Filter out markers with null or invalid coordinates
    const validMarkers = markerData.filter(
      m =>
        m.latitude != null &&
        m.longitude != null &&
        !isNaN(Number(m.latitude)) &&
        !isNaN(Number(m.longitude))
    );

    if (validMarkers.length === 0) {
      return { lat: 0, lng: 0 };
    }

    const avgLat =
      validMarkers.reduce((sum, m) => {
        const lat =
          typeof m.latitude === 'number' ? m.latitude : parseFloat(m.latitude);
        return sum + lat;
      }, 0) / validMarkers.length;
    const avgLng =
      validMarkers.reduce((sum, m) => {
        const lng =
          typeof m.longitude === 'number'
            ? m.longitude
            : parseFloat(m.longitude);
        return sum + lng;
      }, 0) / validMarkers.length;

    return { lat: avgLat, lng: avgLng };
  }, [markerData]);

  if (!apiKey) {
    return (
      <div
        style={{
          borderRadius: 'var(--radius-xl)',
          overflow: 'hidden',
          height: '100%',
          background: '#e5e7eb',
          position: 'relative',
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 'var(--radius-lg)',
            background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
          }}
          className="flex items-center justify-center text-xl text-error"
        >
          Failed to load map!
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        borderRadius: 'var(--radius-xl)',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <APIProvider apiKey={apiKey}>
        <Map
          defaultCenter={center}
          defaultZoom={defaultZoom}
          mapId="map-with-marker"
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 'var(--radius-lg)',
          }}
          gestureHandling="cooperative"
          disableDefaultUI={true}
          mapTypeControl={false}
          fullscreenControl={true}
          streetViewControl={false}
          zoomControl={true}
        >
          {markerData.map((marker, index) => {
            // Skip markers with null or invalid coordinates
            if (
              marker.latitude == null ||
              marker.longitude == null ||
              isNaN(Number(marker.latitude)) ||
              isNaN(Number(marker.longitude))
            ) {
              return null;
            }

            // Convert to numbers if they're strings
            const lat =
              typeof marker.latitude === 'number'
                ? marker.latitude
                : parseFloat(marker.latitude);
            const lng =
              typeof marker.longitude === 'number'
                ? marker.longitude
                : parseFloat(marker.longitude);
            // Check if total score is null to adjust marker size
            const isTotalScoreNull = marker.hotel?.score_alo == null;
            const badgeSize = isTotalScoreNull ? 40 : 64; // 40px (w-10) or 64px (w-16)
            const markerPadding = isTotalScoreNull ? 2 : 4;
            const markerBorder = isTotalScoreNull ? 2 : 3;
            const tailBorderSize = isTotalScoreNull ? 6 : 10;
            const tailHeight = isTotalScoreNull ? 10 : 15;

            const isAloInProgress =
              marker?.hotel?.in_progress ?? globalInProgress;
            const isFastrackInProgress =
              marker?.hotel?.fasttrack_in_progress ?? false;

            // Score availability — checked per viewType
            const hasFastrackScore =
              marker.hotel?.fundamental_awarded_count != null;
            const hasFullScore = marker.hotel?.score_alo != null;

            // Shared in-progress timer marker (amber clock)
            const inProgressMarker = (
              <div
                ref={el => {
                  markerRefs.current[index] = el;
                }}
                className="flex flex-col items-center"
                onMouseEnter={e => {
                  setHoveredMarkerIndex(index);
                  const rect = e.currentTarget.getBoundingClientRect();
                  setTooltipPosition({
                    x: rect.left + rect.width / 2,
                    y: rect.top,
                  });
                }}
                onMouseLeave={() => {
                  setHoveredMarkerIndex(null);
                  setTooltipPosition(null);
                }}
              >
                <div
                  onClick={
                    onMarkerClick ? () => onMarkerClick(marker) : undefined
                  }
                  style={{
                    background: '#FFF7ED',
                    borderRadius: '50%',
                    width: '52px',
                    height: '52px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow:
                      '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
                    border: '3px solid #9e7309',
                    cursor: onMarkerClick ? 'pointer' : 'default',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="26"
                    height="26"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9e7309"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div
                  style={{
                    width: 0,
                    height: 0,
                    borderLeft: `${tailBorderSize}px solid transparent`,
                    borderRight: `${tailBorderSize}px solid transparent`,
                    borderTop: `${tailHeight}px solid #9e7309`,
                    filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.1))',
                  }}
                />
              </div>
            );

            // Shared NA marker (small white circle)
            const naMarker = (
              <div
                ref={el => {
                  markerRefs.current[index] = el;
                }}
                onClick={
                  onMarkerClick ? () => onMarkerClick(marker) : undefined
                }
                onMouseEnter={e => {
                  setHoveredMarkerIndex(index);
                  const rect = e.currentTarget.getBoundingClientRect();
                  setTooltipPosition({
                    x: rect.left + rect.width / 2,
                    y: rect.top,
                  });
                }}
                onMouseLeave={() => {
                  setHoveredMarkerIndex(null);
                  setTooltipPosition(null);
                }}
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: onMarkerClick ? 'pointer' : 'default',
                }}
              >
                <div
                  style={{
                    background: 'white',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow:
                      '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
                    border: '2px solid white',
                  }}
                >
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: 700,
                      color: '#9CA3AF',
                    }}
                  >
                    NA
                  </span>
                </div>
                <div
                  style={{
                    width: 0,
                    height: 0,
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: '10px solid white',
                    filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.1))',
                  }}
                />
              </div>
            );

            // Use custom marker component if provided, otherwise use default
            const markerContent = markerComponent ? (
              markers !== undefined &&
              markers.length > 0 &&
              markerComponent(marker, index)
            ) : viewType === 'fasttrack' ? (
              // ── FastTrack branch ──────────────────────────────────────────
              hasFastrackScore ? (
                // 1. Has score → show fundamental awarded / total
                <div className="flex flex-col items-center">
                  <div
                    onClick={
                      onMarkerClick ? () => onMarkerClick(marker) : undefined
                    }
                    style={{
                      background: 'var(--color-people-blue)',
                      borderRadius: '50%',
                      width: '52px',
                      height: '52px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow:
                        '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
                      border: '3px solid white',
                      cursor: onMarkerClick ? 'pointer' : 'default',
                      fontSize: '13px',
                      fontWeight: 700,
                      color: '#fff',
                      textAlign: 'center',
                    }}
                  >
                    {`${marker.hotel?.fundamental_awarded_count ?? '-'}/${marker.hotel?.fundamental_total ?? '-'}`}
                  </div>
                  <div
                    style={{
                      width: 0,
                      height: 0,
                      borderLeft: `${tailBorderSize}px solid transparent`,
                      borderRight: `${tailBorderSize}px solid transparent`,
                      borderTop: `${tailHeight}px solid white`,
                      filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.1))',
                    }}
                  />
                </div>
              ) : isFastrackInProgress ? (
                // 2. No score + in progress → timer
                inProgressMarker
              ) : (
                // 3. No score + not in progress → NA
                naMarker
              )
            ) : // ── Full view branch ──────────────────────────────────────────
            hasFullScore ? (
              // 1. Has score → ESG badge
              <div
                ref={el => {
                  markerRefs.current[index] = el;
                }}
                onClick={
                  onMarkerClick ? () => onMarkerClick(marker) : undefined
                }
                onMouseEnter={e => {
                  setHoveredMarkerIndex(index);
                  const rect = e.currentTarget.getBoundingClientRect();
                  setTooltipPosition({
                    x: rect.left + rect.width / 2,
                    y: rect.top,
                  });
                }}
                onMouseLeave={() => {
                  setHoveredMarkerIndex(null);
                  setTooltipPosition(null);
                }}
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: onMarkerClick ? 'pointer' : 'default',
                }}
              >
                <div
                  style={{
                    background: 'white',
                    borderRadius: '50%',
                    padding: `${markerPadding}px`,
                    boxShadow:
                      '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
                    border: `${markerBorder}px solid white`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: `${badgeSize}px`,
                    height: `${badgeSize}px`,
                  }}
                >
                  <ESGScoreDisplay
                    scoreE={marker.hotel?.score_e ?? null}
                    scoreS={marker.hotel?.score_s ?? null}
                    scoreG={marker.hotel?.score_g ?? null}
                    scoreAlo={marker.hotel?.score_alo ?? null}
                    isMapMarker={true}
                    className="!w-full"
                  />
                </div>
                <div
                  style={{
                    width: 0,
                    height: 0,
                    borderLeft: `${tailBorderSize}px solid transparent`,
                    borderRight: `${tailBorderSize}px solid transparent`,
                    borderTop: `${tailHeight}px solid white`,
                    filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.1))',
                  }}
                />
              </div>
            ) : isAloInProgress ? (
              // 2. No score + in progress → timer
              inProgressMarker
            ) : (
              // 3. No score + not in progress → NA
              naMarker
            );

            return (
              <AdvancedMarker
                key={`marker-${index}-${lat}-${lng}`}
                position={{ lat, lng }}
              >
                {markerContent}
              </AdvancedMarker>
            );
          })}
        </Map>
      </APIProvider>

      {/* Render tooltip using portal to ensure it's on top of everything */}
      {hoveredMarkerIndex !== null &&
        tooltipPosition &&
        markerData[hoveredMarkerIndex]?.hotel &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              transform: 'translate(-50%, calc(-100% - 12px))',
              background: 'white',
              borderRadius: '8px',
              padding: '12px 16px',
              boxShadow:
                '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              minWidth: '240px',
              maxWidth: '320px',
              zIndex: 999999,
              textTransform: 'capitalize',
              pointerEvents: 'none',
            }}
            className="shadow-lg border border-gray-100"
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: '14px',
                marginBottom: '4px',
                color: '#111827',
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  fontSize: '14px',
                  marginBottom: '4px',
                  color: '#111827',
                }}
              >
                {markerData[hoveredMarkerIndex].hotel?.hotel_location_name ||
                  'Hotel'}
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: '#6B7280',
                  marginBottom: '2px',
                }}
              >
                {markerData[hoveredMarkerIndex].hotel?.city || ''}
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: '#6B7280',
                  marginBottom: '8px',
                }}
              >
                {markerData[hoveredMarkerIndex].hotel?.country || ''}
              </div>
              <div style={{ display: 'flex', gap: '4px', fontSize: '12px' }}>
                {markerData[hoveredMarkerIndex].hotel?.score_e != null && (
                  <div
                    style={{
                      background: 'var(--color-planet-green)',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontWeight: 600,
                    }}
                  >
                    E {markerData[hoveredMarkerIndex].hotel?.score_e}
                  </div>
                )}
                {markerData[hoveredMarkerIndex].hotel?.score_s != null && (
                  <div
                    style={{
                      background: 'var(--color-people-blue)',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontWeight: 600,
                    }}
                  >
                    S {markerData[hoveredMarkerIndex].hotel?.score_s}
                  </div>
                )}
                {markerData[hoveredMarkerIndex].hotel?.score_g != null && (
                  <div
                    style={{
                      background: 'var(--color-policy-orange)',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontWeight: 600,
                    }}
                  >
                    G {markerData[hoveredMarkerIndex].hotel?.score_g}
                  </div>
                )}
                {markerData[hoveredMarkerIndex].hotel?.score_alo != null && (
                  <div
                    style={{
                      background: '#transparent',
                      color: 'var(--color-gray-800)',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontWeight: 600,
                    }}
                    className="border border-gray-400"
                  >
                    Alō score {markerData[hoveredMarkerIndex].hotel?.score_alo}
                  </div>
                )}
                {!(
                  markerData[hoveredMarkerIndex].hotel?.score_e != null ||
                  markerData[hoveredMarkerIndex].hotel?.score_s != null ||
                  markerData[hoveredMarkerIndex].hotel?.score_g != null ||
                  markerData[hoveredMarkerIndex].hotel?.score_alo != null
                ) &&
                  ((
                    viewType === 'fasttrack'
                      ? markerData[hoveredMarkerIndex].hotel
                          ?.fasttrack_in_progress
                      : (markerData[hoveredMarkerIndex].hotel?.in_progress ??
                        globalInProgress)
                  ) ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: '#9e7309',
                        fontWeight: 500,
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#9e7309"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      In Progress
                    </div>
                  ) : (
                    <div className="">Not scored yet</div>
                  ))}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
